import normalize from 'json-api-normalizer';
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { JobHistoryEventRecord } from 'Douleutaras/redux/data/history/types';
import { makeRequest } from 'Douleutaras/services/apiClient';

export const JobHistoryEndpoint = jobId => `job/${jobId}/history`;

const jobHistoryAdapter = createEntityAdapter<JSONApiDataObject<JobHistoryEventRecord>>();

interface JobHistoryNormalizedEntityList {
  jobHistoryEventRecords: { [index in number]: JSONApiDataObject<JobHistoryEventRecord> };
  meta: {};
}

export const fetchJobHistory = createAsyncThunk<JobHistoryNormalizedEntityList, { jobId: string }>(
  'jobHistory/getAllByJobId',
  async ({ jobId }, api) => {
    const endpoint = JobHistoryEndpoint(jobId);
    const response = await makeRequest(`api/public/v3/${endpoint}`, {
      additionalGetParams: {
        'page[size]': '100'
      }
    })(api);

    return normalize(response.data, { endpoint });
  });

export const jobHistorySlice = createSlice({
  name: 'jobHistory',
  initialState: jobHistoryAdapter.getInitialState<ReduxEntity<JobHistoryEventRecord>>({
    loading: false,
    error: false,
    entities: {},
    meta: {
      pagination: {
        count: undefined,
        page: undefined,
        pages: undefined
      },
      data: [],
      meta: {}
    }
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchJobHistory.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchJobHistory.rejected, state => {
      state.loading = false;
    });
    builder.addCase(fetchJobHistory.fulfilled, (state, action) => {
      if (action.payload.jobHistoryEventRecords) {
        jobHistoryAdapter.setMany(state, action.payload.jobHistoryEventRecords);
      }
      state.meta = { ...state.meta, ...action.payload.meta };
      state.loading = false;
    });
  }
});

export const jobHistorySelectors = jobHistoryAdapter.getSelectors((state: RootState) => state.jobHistory);

// Select a job's history list based on job id
export const selectHistoryByJobId = createSelector([
  state => state.jobHistory,
  (state, { jobId }) => ({ jobId })
], (jobHistory, { jobId }) => {

  const metaKey = JobHistoryEndpoint(jobId);
  const metaJobHistory = jobHistory.meta[metaKey]?.data;

  return (metaJobHistory || []).map(metaJob => jobHistory.entities[metaJob.id]);
});
